import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { api } from '../../services/api';
import { ChampMesure } from '../../mocks/mesures';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';
import { Toast } from '../../components/base/Toast';

function getUser() {
  const s = localStorage.getItem('atelier_bassoum_user');
  return s ? JSON.parse(s) : { nom: 'Utilisateur', role: 'gerant' };
}

const DEFAULT_UNITES = ['cm', 'mm', 'pouces', 'kg', 'g'];

interface Unite {
  id: string;
  valeur: string;
}

type TabType = 'champs' | 'unites';

export default function ParametresMesures() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<TabType>('champs');
  const [champs, setChamps] = useState<ChampMesure[]>([]);
  const [unites, setUnites] = useState<Unite[]>(DEFAULT_UNITES.map((u, i) => ({ id: `u${i}`, valeur: u })));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newUniteVal, setNewUniteVal] = useState('');
  const [editUniteId, setEditUniteId] = useState<string | null>(null);
  const [editUniteVal, setEditUniteVal] = useState('');
  const [deleteUniteId, setDeleteUniteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchChamps(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchChamps = async (token: string) => {
    try {
      const data = await api.getMesureChamps(token);
      if (Array.isArray(data)) {
        setChamps(data.map((c: any) => ({
            ...c,
            id: String(c.id),
            obligatoire: Boolean(Number(c.obligatoire)),
            illustration: c.illustration || ''
        })));
      } else {
        setChamps([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [newChamp, setNewChamp] = useState<Partial<ChampMesure>>({
    nom: '', unite: 'cm', description: '', illustration: '', obligatoire: false,
  });

  const uniteValeurs = unites.map(u => u.valeur);

  const handleEdit = (champ: ChampMesure) => setEditingId(champ.id);
  
  const handleSaveEdit = async (id: string, updated: Partial<ChampMesure>) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      const champ = champs.find(c => c.id === id);
      if (!champ) return;
      await api.saveMesureChamp(token, { ...champ, ...updated, id: Number(id) });
      setChamps(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deleteMesureChamp(token, Number(id));
      setChamps(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleObligatoire = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    const champ = champs.find(c => c.id === id);
    if (!champ) return;
    try {
      await api.saveMesureChamp(token, { ...champ, obligatoire: !champ.obligatoire ? 1 : 0, id: Number(id) });
      setChamps(prev => prev.map(c => c.id === id ? { ...c, obligatoire: !c.obligatoire } : c));
    } catch (err) {
      console.error(err);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const nc = [...champs];
    [nc[index - 1], nc[index]] = [nc[index], nc[index - 1]];
    const newChamps = nc.map((c, i) => ({ ...c, ordre: i + 1 }));
    setChamps(newChamps);
    saveOrder(newChamps);
  };

  const handleMoveDown = async (index: number) => {
    if (index === champs.length - 1) return;
    const nc = [...champs];
    [nc[index], nc[index + 1]] = [nc[index + 1], nc[index]];
    const newChamps = nc.map((c, i) => ({ ...c, ordre: i + 1 }));
    setChamps(newChamps);
    saveOrder(newChamps);
  };

  const saveOrder = async (newList: ChampMesure[]) => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
          const orderData = newList.map((c, i) => ({ id: Number(c.id), ordre: i + 1 }));
          await api.saveMesureChampsOrder(token, orderData);
          showToast("Ordre sauvegardé");
      } catch (err) {
          console.error(err);
          showToast("Erreur sauvegarde ordre", "error");
      }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 3000);
  };

  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  const handleAddChamp = async () => {
    if (!newChamp.nom) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    
    try {
      const res = await api.saveMesureChamp(token, {
          nom: newChamp.nom,
          unite: newChamp.unite,
          description: newChamp.description,
          illustration_url: newChamp.illustration,
          obligatoire: newChamp.obligatoire ? 1 : 0,
          ordre: champs.length + 1
      });
      fetchChamps(token); // Refresh from server
      setNewChamp({ nom: '', unite: 'cm', description: '', illustration: '', obligatoire: false });
      setShowAddForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddUnite = () => {
    const trimmed = newUniteVal.trim();
    if (!trimmed || unites.find(u => u.valeur.toLowerCase() === trimmed.toLowerCase())) return;
    setUnites(prev => [...prev, { id: `u${Date.now()}`, valeur: trimmed }]);
    setNewUniteVal('');
  };

  const handleSaveUnite = (id: string) => {
    const trimmed = editUniteVal.trim();
    if (!trimmed) return;
    setUnites(prev => prev.map(u => u.id === id ? { ...u, valeur: trimmed } : u));
    setEditUniteId(null);
  };

  const handleDeleteUnite = (id: string) => {
    setUnites(prev => prev.filter(u => u.id !== id));
    setDeleteUniteId(null);
  };

  const handleSaveAll = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // GARDE NULL: Empêche le rendu si l'utilisateur n'est pas encore chargé
  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user?.role || 'gerant'} nom={user?.nom || 'Utilisateur'} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/mesures')} title="Retour aux mesures" className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-gray-600" />
            </button>
            <div>
              <h1 className="font-playfair font-bold text-lg text-gray-900">Paramètres de mesures</h1>
              <p className="text-xs text-gray-400 mt-0.5">{champs.length} champs · {unites.length} unités</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'champs' && (
              <button
                onClick={() => setShowAddForm(true)}
                className="hidden md:flex items-center gap-2 text-xs bg-gray-100 text-gray-600 px-3 py-2 rounded-xl font-semibold cursor-pointer hover:bg-gray-200 whitespace-nowrap"
              >
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line" /></div>
                Nouveau champ
              </button>
            )}
            <button
              onClick={handleSaveAll}
              className={`flex items-center gap-2 text-xs px-4 py-2 rounded-xl font-semibold cursor-pointer whitespace-nowrap transition-all ${saved ? 'bg-foret-100 text-foret-700' : 'bg-foret-500 text-white hover:bg-foret-600'}`}
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={saved ? 'ri-check-line' : 'ri-save-line'} />
              </div>
              {saved ? <span className="hidden sm:inline">Enregistré !</span> : 'Enregistrer'}
              {saved && <span className="sm:hidden">OK</span>}
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-100 px-6 flex gap-1 py-2">
          {([['champs', 'Champs de mesure', 'ri-ruler-line'], ['unites', 'Unités de mesure', 'ri-scales-line']] as const).map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === key ? 'bg-foret-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={icon} /></div>
              {label}
            </button>
          ))}
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {activeTab === 'champs' && (
              <>
                <div className="bg-or-50 border border-or-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-or-600 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm" />
                  </div>
                  <p className="text-xs text-or-700">Ces champs sont utilisés dans toutes les fiches de mesures clients et dans le formulaire de commande. Les champs obligatoires sont marqués d&apos;un astérisque.</p>
                </div>

                {showAddForm && (
                  <div className="bg-foret-50 border-2 border-foret-200 border-dashed rounded-2xl p-5">
                    <h3 className="font-semibold text-foret-800 mb-4 text-sm">Nouveau champ de mesure</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="new-champ-nom" className="block text-xs font-semibold text-gray-700 mb-1">Nom du champ *</label>
                        <input
                          id="new-champ-nom"
                          type="text"
                          value={newChamp.nom}
                          onChange={e => setNewChamp(p => ({ ...p, nom: e.target.value }))}
                          placeholder="ex: Tour de cou"
                          title="Nom du nouveau champ de mesure"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="new-champ-unite" className="block text-xs font-semibold text-gray-700 mb-1">Unité</label>
                        <select
                          id="new-champ-unite"
                          value={newChamp.unite}
                          onChange={e => setNewChamp(p => ({ ...p, unite: e.target.value }))}
                          title="Choisir l'unité de mesure"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white cursor-pointer"
                        >
                          {uniteValeurs.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label htmlFor="new-champ-desc" className="block text-xs font-semibold text-gray-700 mb-1">Description (optionnelle)</label>
                        <input
                          id="new-champ-desc"
                          type="text"
                          value={newChamp.description}
                          onChange={e => setNewChamp(p => ({ ...p, description: e.target.value }))}
                          placeholder="ex: Mesurer autour du cou sans serrer"
                          title="Description du nouveau champ"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <label htmlFor="new-champ-illust-url" className="block text-xs font-semibold text-gray-700 mb-1.5">Illustration</label>
                        <IllustrationInput
                          id="new-champ-illust-url"
                          value={newChamp.illustration || ''}
                          onChange={v => setNewChamp(p => ({ ...p, illustration: v }))}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="new-champ-obligatoire" className="flex items-center gap-2 cursor-pointer">
                        <input
                          id="new-champ-obligatoire"
                          type="checkbox"
                          checked={!!newChamp.obligatoire}
                          onChange={e => setNewChamp(p => ({ ...p, obligatoire: e.target.checked }))}
                          title="Rendre ce champ obligatoire"
                          className="w-4 h-4 rounded accent-foret-500"
                        />
                        <span className="text-sm text-gray-700">Champ obligatoire</span>
                      </label>
                      <div className="flex gap-2">
                        <button onClick={() => setShowAddForm(false)} className="text-xs px-3 py-2 rounded-xl bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 whitespace-nowrap">Annuler</button>
                        <button onClick={handleAddChamp} disabled={!newChamp.nom} className="text-xs px-4 py-2 rounded-xl bg-foret-500 text-white font-semibold cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap">Ajouter</button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {/* Mobile Fields List (Cards) */}
                  <div className="md:hidden divide-y divide-gray-50 pb-20">
                    {champs.map((champ, index) => (
                      <div key={champ.id} className="p-4 bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-black text-gray-300 w-4">#{index + 1}</span>
                            <span className="text-sm font-black text-gray-900 uppercase tracking-tight">{champ.nom}</span>
                            {champ.obligatoire && <span className="text-red-500 font-black text-xs">*</span>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => handleMoveUp(index)} disabled={index === 0} title="Monter le champ" className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 text-gray-400 disabled:opacity-20"><i className="ri-arrow-up-s-line" /></button>
                            <button onClick={() => handleMoveDown(index)} disabled={index === champs.length - 1} title="Descendre le champ" className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 text-gray-400 disabled:opacity-20"><i className="ri-arrow-down-s-line" /></button>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 mb-4">
                          {champ.illustration && (
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 border border-gray-50 shrink-0">
                               <img src={champ.illustration} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                             <p className="text-xs text-gray-400 line-clamp-1 mb-1">{champ.description || 'Pas de description'}</p>
                             <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-widest">{champ.unite}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                           <button
                             onClick={() => handleToggleObligatoire(champ.id)}
                             className={`flex-1 py-2.5 rounded-xl border font-black text-[10px] uppercase tracking-widest transition-all ${champ.obligatoire ? 'bg-foret-50 border-foret-200 text-foret-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                           >
                             {champ.obligatoire ? 'Obligatoire' : 'Facultatif'}
                           </button>
                           <button
                             onClick={() => handleEdit(champ)}
                             title="Modifier le champ"
                             className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-900 text-white shrink-0"
                           >
                             <i className="ri-edit-line" />
                           </button>
                           <button
                             onClick={() => { if(window.confirm('Supprimer ce champ ?')) handleDelete(champ.id); }}
                             title="Supprimer le champ"
                             className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 border border-red-50 shrink-0"
                           >
                             <i className="ri-delete-bin-line" />
                           </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        <div className="col-span-1">#</div>
                        <div className="col-span-4">Nom</div>
                        <div className="col-span-2">Unité</div>
                        <div className="col-span-3">Description</div>
                        <div className="col-span-1 text-center">Oblig.</div>
                        <div className="col-span-1 text-right">Actions</div>
                      </div>
                    </div>

                    {champs.map((champ, index) => (
                      <ChampRow
                        key={champ.id}
                        champ={champ}
                        index={index}
                        isEditing={editingId === champ.id}
                        onEdit={() => handleEdit(champ)}
                        onSave={(updated) => handleSaveEdit(champ.id, updated)}
                        onDelete={() => handleDelete(champ.id)}
                        onToggleObligatoire={() => handleToggleObligatoire(champ.id)}
                        onMoveUp={() => handleMoveUp(index)}
                        onMoveDown={() => handleMoveDown(index)}
                        isFirst={index === 0}
                        isLast={index === champs.length - 1}
                        uniteValeurs={uniteValeurs}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'unites' && (
              <>
                <div className="bg-or-50 border border-or-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-or-600 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm" />
                  </div>
                  <p className="text-xs text-or-700">Les unités ajoutées ici seront disponibles dans les listes déroulantes lors de la création ou modification des champs de mesure.</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Ajouter une unité</p>
                    <div className="flex gap-2">
                      <input
                        id="new-unite-input"
                        type="text"
                        value={newUniteVal}
                        onChange={e => setNewUniteVal(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddUnite()}
                        placeholder="ex: pieds, yards, oz…"
                        title="Nom de la nouvelle unité"
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
                      />
                      <button
                        onClick={handleAddUnite}
                        disabled={!newUniteVal.trim()}
                        className="px-4 py-2 bg-foret-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>

                  {unites.map(u => (
                    <div key={u.id} className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                      <div className="w-8 h-8 bg-foret-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-ruler-2-line text-foret-500 text-sm" />
                      </div>
                      {editUniteId === u.id ? (
                        <>
                          <input
                            id={`edit-unite-val-${u.id}`}
                            autoFocus
                            type="text"
                            value={editUniteVal}
                            onChange={e => setEditUniteVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveUnite(u.id); if (e.key === 'Escape') setEditUniteId(null); }}
                            placeholder="Unité"
                            title="Modifier la valeur de l'unité"
                            className="flex-1 px-2 py-1 text-sm border border-foret-300 rounded-xl focus:outline-none bg-white"
                          />
                          <button onClick={() => handleSaveUnite(u.id)} className="text-xs px-3 py-1 bg-foret-500 text-white rounded-lg cursor-pointer font-semibold whitespace-nowrap">OK</button>
                          <button onClick={() => setEditUniteId(null)} className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-lg cursor-pointer whitespace-nowrap">✕</button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium text-gray-800">{u.valeur}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                            {champs.filter(c => c.unite === u.valeur).length} champ(s)
                          </span>
                          {deleteUniteId === u.id ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-500">Supprimer ?</span>
                              <button onClick={() => handleDeleteUnite(u.id)} className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-lg cursor-pointer whitespace-nowrap">Oui</button>
                              <button onClick={() => setDeleteUniteId(null)} className="text-xs px-2 py-0.5 bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">Non</button>
                            </div>
                          ) : (
                            <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { setEditUniteId(u.id); setEditUniteVal(u.valeur); }}
                                title="Modifier l'unité"
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:border-foret-300 hover:text-foret-600 text-gray-400 cursor-pointer transition-colors"
                              >
                                <i className="ri-pencil-line text-xs" />
                              </button>
                              <button
                                onClick={() => setDeleteUniteId(u.id)}
                                title="Supprimer l'unité"
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:border-red-300 hover:text-red-500 text-gray-400 cursor-pointer transition-colors"
                              >
                                <i className="ri-delete-bin-line text-xs" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {activeTab === 'champs' && (
        <FloatingActionButton 
          onClick={() => setShowAddForm(true)} 
          icon="ri-add-line" 
          label="Nouveau champ" 
        />
      )}
    </div>
  );
}

// --- IllustrationInput: URL + file upload ---
interface IllustrationInputProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
}

function IllustrationInput({ id, value, onChange }: IllustrationInputProps) {
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => onChange(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          value={(value && typeof value === 'string' && value.startsWith('data:')) ? '' : (value || '')}
          onChange={e => onChange(e.target.value)}
          placeholder="Coller une URL https://…"
          title="URL de l'illustration"
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
        />
        <label htmlFor={`${id}-file`} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-gray-100 text-gray-600 rounded-xl cursor-pointer hover:bg-gray-200 whitespace-nowrap transition-colors">
          <input id={`${id}-file`} type="file" accept="image/*" className="hidden" onChange={handleFile} title="Télécharger une image" />
          <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-upload-2-line" /></div>
          Parcourir
        </label>
      </div>
      {value && (
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
            <img src={value} alt="Aperçu" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500">Aperçu de l&apos;illustration</p>
            <button onClick={() => onChange('')} className="text-xs text-red-400 hover:underline cursor-pointer">Supprimer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- ChampRow component ---
interface ChampRowProps {
  champ: ChampMesure;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (updated: Partial<ChampMesure>) => void;
  onDelete: () => void;
  onToggleObligatoire: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  uniteValeurs: string[];
}

function ChampRow({ champ, index, isEditing, onEdit, onSave, onDelete, onToggleObligatoire, onMoveUp, onMoveDown, isFirst, isLast, uniteValeurs }: ChampRowProps) {
  const [editNom, setEditNom] = useState(champ.nom || '');
  const [editUnite, setEditUnite] = useState(champ.unite || 'cm');
  const [editDesc, setEditDesc] = useState(champ.description || '');
  const [editIllust, setEditIllust] = useState(champ.illustration || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleIllustFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setEditIllust(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  if (isEditing) {
    return (
      <div className="px-5 py-4 border-b border-gray-50 bg-foret-50/50">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="edit-field-nom" className="text-xs text-gray-500 mb-1 block">Nom</label>
            <input id="edit-field-nom" value={editNom} onChange={e => setEditNom(e.target.value)} placeholder="Nom" title="Nom du champ" className="w-full px-3 py-2 text-sm border border-foret-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white" />
          </div>
          <div>
            <label htmlFor="edit-field-unite" className="text-xs text-gray-500 mb-1 block">Unité</label>
            <select id="edit-field-unite" value={editUnite} onChange={e => setEditUnite(e.target.value)} title="Unité du champ" className="w-full px-3 py-2 text-sm border border-foret-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white cursor-pointer">
              {uniteValeurs.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="edit-field-desc" className="text-xs text-gray-500 mb-1 block">Description</label>
            <input id="edit-field-desc" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" title="Description du champ" className="w-full px-3 py-2 text-sm border border-foret-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white" />
          </div>
          <div>
            <label htmlFor="edit-field-illust-url" className="text-xs text-gray-500 mb-1 block">Illustration</label>
            <div className="flex gap-2">
              <input
                id="edit-field-illust-url"
                type="text"
                value={editIllust && editIllust.startsWith('data:') ? '' : editIllust}
                onChange={e => setEditIllust(e.target.value)}
                placeholder="URL https://…"
                title="URL de l'illustration"
                className="flex-1 px-3 py-1.5 text-sm border border-foret-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
              />
              <label htmlFor="edit-field-illust-file" className="flex items-center gap-1 px-2 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-xl cursor-pointer hover:bg-gray-200 whitespace-nowrap transition-colors">
                <input id="edit-field-illust-file" type="file" accept="image/*" className="hidden" onChange={handleIllustFile} title="Télécharger une image" />
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-upload-2-line" /></div>
              </label>
            </div>
            {editIllust && (
              <div className="mt-1 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                  <img src={editIllust} alt="aperçu" className="w-full h-full object-cover" />
                </div>
                <button 
                  onClick={() => setEditIllust('')}
                  className="text-[10px] text-red-500 hover:underline cursor-pointer font-semibold"
                >
                  Supprimer
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => onSave({})} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 whitespace-nowrap">Annuler</button>
          <button onClick={() => onSave({ nom: editNom, unite: editUnite, description: editDesc, illustration: editIllust })} className="text-xs px-4 py-1.5 rounded-lg bg-foret-500 text-white font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">Enregistrer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors group">
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-1 flex items-center gap-1">
          <div className="flex flex-col gap-0.5">
            <button onClick={onMoveUp} disabled={isFirst} title="Monter" className="w-4 h-3 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
              <i className="ri-arrow-up-s-line text-xs" />
            </button>
            <button onClick={onMoveDown} disabled={isLast} title="Descendre" className="w-4 h-3 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer">
              <i className="ri-arrow-down-s-line text-xs" />
            </button>
          </div>
          <span className="text-xs text-gray-300 font-mono">{index + 1}</span>
        </div>
        <div className="col-span-4 flex items-center gap-2">
          {champ.illustration && (
            <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
              <img src={champ.illustration} alt={champ.nom} className="w-full h-full object-cover" />
            </div>
          )}
          <span className="text-sm font-semibold text-gray-800">{champ.nom}</span>
          {champ.obligatoire && <span className="text-red-400 text-xs">*</span>}
        </div>
        <div className="col-span-2">
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{champ.unite}</span>
        </div>
        <div className="col-span-3">
          <p className="text-xs text-gray-400 truncate">{champ.description || '—'}</p>
        </div>
        <div className="col-span-1 flex justify-center">
          <button
            onClick={onToggleObligatoire}
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${champ.obligatoire ? 'bg-foret-500 border-foret-500' : 'border-gray-300 hover:border-foret-300'}`}
          >
            {champ.obligatoire && <i className="ri-check-line text-white text-xs" />}
          </button>
        </div>
        <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} title="Modifier" className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-foret-100 text-gray-400 hover:text-foret-600 cursor-pointer transition-colors">
            <i className="ri-pencil-line text-xs" />
          </button>
          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)} title="Supprimer" className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
              <i className="ri-delete-bin-line text-xs" />
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button onClick={() => setShowDeleteConfirm(false)} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 cursor-pointer whitespace-nowrap">Non</button>
              <button onClick={onDelete} className="text-xs px-2 py-0.5 rounded bg-red-500 text-white font-semibold cursor-pointer whitespace-nowrap">Oui</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
